import React, { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Button, type ButtonProps } from './Button';
import { useSentry } from '../../contexts/SentryContext';
import { playSound, SoundType } from '~/lib/audio';

function getTextContent(
  children: React.ReactNode
): string | null {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    const texts = children
      .map(getTextContent)
      .filter(Boolean);
    return texts.length > 0 ? texts.join(' ') : null;
  }
  return null;
}

type TrackedButtonProps = ButtonProps & {
  trackingId?: string;
  'data-testid'?: string;
  title?: string;
};

export const TrackedButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  TrackedButtonProps
>(
  (
    {
      trackingId,
      onClick,
      children,
      'data-testid': testId,
      title,
      ...props
    },
    ref
  ) => {
    const { addBreadcrumb } = useSentry();

    const handleClick = (
      e: React.MouseEvent<
        HTMLButtonElement | HTMLAnchorElement
      >
    ) => {
      const label =
        trackingId ||
        testId ||
        title ||
        getTextContent(children) ||
        'unknown-button';
      addBreadcrumb(`Click: ${label}`, {
        element: 'button',
        testId,
        title,
      });
      onClick?.(e as any);
    };

    return (
      <Button
        ref={ref}
        onClick={handleClick}
        data-testid={testId}
        title={title}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

TrackedButton.displayName = 'TrackedButton';

interface TrackedLinkProps extends LinkProps {
  trackingId?: string;
  'data-testid'?: string;
  children?: React.ReactNode;
}

export const TrackedLink = ({
  trackingId,
  to,
  onClick,
  children,
  'data-testid': testId,
  ...props
}: TrackedLinkProps) => {
  const { addBreadcrumb } = useSentry();

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    const label =
      trackingId ||
      testId ||
      getTextContent(children) ||
      to.toString();
    addBreadcrumb(`Navigate: ${label}`, {
      element: 'link',
      to: to.toString(),
      testId,
    });
    playSound(SoundType.Navigate);
    onClick?.(e);
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      data-testid={testId}
      {...props}
    >
      {children}
    </Link>
  );
};

TrackedLink.displayName = 'TrackedLink';
